import { useRef, useState, useEffect, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    InformationCircleIcon,
    ArchiveBoxIcon,
    DocumentTextIcon,
    ArrowPathIcon,
    ArrowDownTrayIcon,
    CodeBracketIcon,
    XMarkIcon,
    TrashIcon,
    PencilIcon

} from '@heroicons/react/24/outline'
import { colorMap, formatDate } from '../../../Common'
import { toast } from 'react-toastify'
import moment from 'moment'

const FaturasTable = ({ data, loading, title, headers, viewFatura, color = 'purple', previous, next, page, isAdmin = false, geracaoNovaFatura, downloadFaturaPDF, deleteFatura, editFatura }) => {
    const [geracaoLoadingId, setGeracaoLoadingId] = useState(null)
    const [downloadLoadingId, setDownloadLoadingId] = useState(null)
    const colorClasses = colorMap[color] || colorMap.purple

    // Estado para controlar o modal de seleção de download
    const [showDownloadModal, setShowDownloadModal] = useState(false)
    const [downloadData, setDownloadData] = useState(null)
    const [baseFileName, setBaseFileName] = useState('')
    const [downloadType, setDownloadType] = useState(null) // 'download' ou 'geracao'

    const tableScrollRef = useRef(null)
    const tableContainerRef = useRef(null)
    const [showLeftArrow, setShowLeftArrow] = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(true)
    const [arrowTop, setArrowTop] = useState('50%')

    const scrollTable = (direction) => {
        if (tableScrollRef.current) {
            const scrollAmount = 300
            const newScrollLeft = tableScrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount)
            tableScrollRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth'
            })
        }
    }

    const checkScrollPosition = () => {
        if (tableScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current
            const isAtStart = scrollLeft <= 1
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1

            setShowLeftArrow(!isAtStart)
            setShowRightArrow(!isAtEnd)
        }
    }

    const calculateArrowPosition = () => {
        if (tableContainerRef.current && tableScrollRef.current) {
            const containerRect = tableContainerRef.current.getBoundingClientRect()
            const scrollRect = tableScrollRef.current.getBoundingClientRect()
            const headerHeight = scrollRect.top - containerRect.top
            const tableHeight = scrollRect.height
            const topPosition = headerHeight + (tableHeight / 2)
            const containerHeight = containerRect.height
            const percentage = (topPosition / containerHeight) * 100
            setArrowTop(`${percentage}%`)
        }
    }

    const formatCurrency = (value) => {
        const valueFloat = (value / 100)
        return parseFloat(valueFloat).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        })
    }

    const formatCNPJ = (cnpj) => {
        if (!cnpj) return '-'
        return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    }

    const formatPhone = (phone) => {
        if (!phone) return '-'
        return phone.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3')
    }

    const formatDateOptional = (date) => {
        if (!date) return '-'
        return formatDate(date)
    }

    const downloadFile = (base64Data, fileName, mimeType) => {
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: mimeType })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(link.href)
    }

    // Chama a API de download e abre o modal de seleção
    const handleDownload = async (item) => {
        if (!downloadFaturaPDF) {
            toast.error('Função de download não disponível')
            return
        }

        setDownloadLoadingId(item.id)
        try {
            const response = await downloadFaturaPDF(item.id)

            if (response) {
                const fileName = `fatura_${item.numero_referencia}`
                setDownloadData(response)
                setBaseFileName(fileName)
                setDownloadType('download')
                setShowDownloadModal(true)
            } else {
                toast.error('Erro ao baixar fatura')
            }
        } catch (error) {
            console.error('Erro ao baixar fatura:', error)
            toast.error('Erro ao baixar fatura')
        } finally {
            setDownloadLoadingId(null)
        }
    }

    // Chama a API de geração e abre o modal de seleção
    const handleGerarNovaVersao = async (item) => {
        if (!geracaoNovaFatura) {
            toast.error('Função de geração não disponível')
            return
        }

        setGeracaoLoadingId(item.id)
        try {
            const response = await geracaoNovaFatura(item.id)

            if (response) {
                const fileName = `fatura_${item.numero_referencia}_nova_versao`
                setDownloadData(response)
                setBaseFileName(fileName)
                setDownloadType('geracao')
                setShowDownloadModal(true)
            } else {
                toast.error('Erro ao gerar nova versão')
            }
        } catch (error) {
            console.error('Erro ao gerar nova versão:', error)
            toast.error('Erro ao gerar nova versão da fatura')
        } finally {
            setGeracaoLoadingId(null)
        }
    }

    // Executa o download baseado na seleção do usuário
    const executeDownload = (type) => {
        if (!downloadData) return

        if (type === 'pdf' || type === 'ambos') {
            if (downloadData.pdf) {
                downloadFile(downloadData.pdf, `${baseFileName}.pdf`, 'application/pdf')
            }
        }

        if (type === 'xml' || type === 'ambos') {
            if (downloadData.xml) {
                downloadFile(downloadData.xml, `${baseFileName}.xml`, 'application/xml')
            }
        }

        const successMessage = downloadType === 'geracao'
            ? 'Nova versão gerada e download iniciado!'
            : 'Download iniciado com sucesso!'

        toast.success(successMessage)
        closeDownloadModal()
    }

    const handlerDelete = async (item) => {
        if (window.confirm('Tem certeza que deseja excluir esta fatura?')) {
            await deleteFatura(item.id)
        }
    }

    const handlerUpdate = async (item) => {
        editFatura(item)
    }

    const closeDownloadModal = () => {
        setShowDownloadModal(false)
        setDownloadData(null)
        setBaseFileName('')
        setDownloadType(null)
    }

    useEffect(() => {
        const tableElement = tableScrollRef.current
        if (tableElement) {
            const timer = setTimeout(() => {
                checkScrollPosition()
                calculateArrowPosition()
            }, 1)

            tableElement.addEventListener('scroll', checkScrollPosition)
            window.addEventListener('resize', () => {
                checkScrollPosition()
                calculateArrowPosition()
            })

            return () => {
                clearTimeout(timer)
                tableElement.removeEventListener('scroll', checkScrollPosition)
                window.removeEventListener('resize', checkScrollPosition)
            }
        }
    }, [data, loading])

    return (
        <>
            <div className="relative" ref={tableContainerRef}>
                {showLeftArrow && (
                    <button
                        onClick={() => scrollTable('left')}
                        className={`absolute -left-14 -translate-y-1/2 z-30 p-2.5 rounded-full ${colorClasses.headerButtonBg} text-white shadow-lg hover:opacity-90 transition-all duration-200 hidden lg:flex items-center justify-center`}
                        title="Rolar para a esquerda"
                        style={{ top: arrowTop }}
                    >
                        <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                )}

                {showRightArrow && (
                    <button
                        onClick={() => scrollTable('right')}
                        className={`absolute -right-14 -translate-y-1/2 z-30 p-2.5 rounded-full ${colorClasses.headerButtonBg} text-white shadow-lg hover:opacity-90 transition-all duration-200 hidden lg:flex items-center justify-center`}
                        title="Rolar para a direita"
                        style={{ top: arrowTop }}
                    >
                        <ChevronRightIcon className="w-5 h-5" />
                    </button>
                )}

                <div className="bg-white rounded-2xl shadow-lg overflow-hidden relative">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    </div>

                    <div className="relative">
                        <div
                            ref={tableScrollRef}
                            className="overflow-x-auto scrollbar-hide"
                            style={{
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                WebkitOverflowScrolling: 'touch'
                            }}
                            onScroll={checkScrollPosition}
                        >
                            <style>{`
                                    .scrollbar-hide::-webkit-scrollbar {
                                        display: none;
                                    }
                                `}</style>

                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                                        <p className="mt-4 text-gray-600">Carregando {title}...</p>
                                    </div>
                                </div>
                            ) : data && data.length > 0 ? (
                                <>
                                    <div className="hidden lg:block px-3 py-2 bg-gray-50 border-b border-gray-200">
                                        <div className="grid grid-cols-7 gap-2 items-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                                            {headers.map((header, index) => (
                                                <div key={header.key || index} className="flex items-center justify-center">
                                                    <header.icon className="w-3 h-3 mr-1" />
                                                    {header.label}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="divide-y divide-gray-200">
                                        {data.map((item) => {
                                            return (
                                                <div
                                                    key={item.id}
                                                    className="px-3 py-2 hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    <div className="hidden lg:block">
                                                        <div className="grid grid-cols-7 gap-2 items-center">
                                                            <div className="flex items-center justify-center gap-1">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        viewFatura(item)
                                                                    }}
                                                                    className={`p-1.5 ${colorClasses.headerIconBg} ${colorClasses.headerIconText} rounded-lg hover:opacity-80 transition-all duration-200 transform hover:scale-110`}
                                                                    title="Visualizar detalhes"
                                                                >
                                                                    <EyeIcon className="w-4 h-4" />
                                                                </button>

                                                                {downloadFaturaPDF && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            handleDownload(item)
                                                                        }}
                                                                        disabled={downloadLoadingId === item.id}
                                                                        className={`p-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all duration-200 transform hover:scale-110 disabled:opacity-50`}
                                                                        title="Baixar fatura"
                                                                    >
                                                                        {downloadLoadingId === item.id ? (
                                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                                                                        ) : (
                                                                            <ArrowDownTrayIcon className="w-4 h-4" />
                                                                        )}
                                                                    </button>
                                                                )}

                                                                {geracaoNovaFatura && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            handleGerarNovaVersao(item)
                                                                        }}
                                                                        disabled={geracaoLoadingId === item.id}
                                                                        className={`p-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-all duration-200 transform hover:scale-110 disabled:opacity-50`}
                                                                        title="Gerar nova versão"
                                                                    >
                                                                        {geracaoLoadingId === item.id ? (
                                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                                                                        ) : (
                                                                            <ArrowPathIcon className="w-4 h-4" />
                                                                        )}
                                                                    </button>
                                                                )}

                                                                {editFatura && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            handlerUpdate(item)
                                                                        }}
                                                                        className={`p-1.5 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-all duration-200 transform hover:scale-110`}
                                                                        title="Editar fatura"
                                                                    >
                                                                        <PencilIcon className="w-4 h-4" />
                                                                    </button>
                                                                )}

                                                                {deleteFatura && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            handlerDelete(item)
                                                                        }}
                                                                        className={`p-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all duration-200 transform hover:scale-110`}
                                                                        title="Excluir fatura"
                                                                    >
                                                                        <TrashIcon className="w-4 h-4" />
                                                                    </button>
                                                                )}
                                                            </div>

                                                            <div className="text-center">
                                                                <div className="text-xs font-medium text-gray-900 truncate" title={item.numero_referencia}>
                                                                    {`${item.numero_referencia}/${moment(item.data_emissao).format('YYYY')}`}
                                                                </div>
                                                            </div>

                                                            <div className="text-center">
                                                                <div className="text-xs font-medium text-gray-900 truncate" title={item.cliente_nome_fantasia}>
                                                                    {item.cliente_nome_fantasia}
                                                                </div>
                                                            </div>

                                                            <div className="text-center">
                                                                <div className="text-xs font-medium text-gray-900 truncate" title={formatCNPJ(item.cliente_cnpj)}>
                                                                    {formatCNPJ(item.cliente_cnpj)}
                                                                </div>
                                                            </div>

                                                            <div className="text-center">
                                                                <div className="text-xs font-medium text-gray-900">
                                                                    {formatDateOptional(item.data_fatura)}
                                                                </div>
                                                            </div>

                                                            <div className="text-center">
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    {formatCurrency(item.total)}
                                                                </span>
                                                            </div>

                                                            <div className="text-center">
                                                                <div className="text-xs font-medium text-gray-900">
                                                                    {formatDateOptional(item.data_criacao)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="lg:hidden">
                                                        <div className="space-y-3">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex-1 min-w-0">
                                                                    <h3 className="text-sm font-semibold text-gray-900 truncate">Fatura #{item.numero_referencia}</h3>
                                                                    <p className="text-xs text-gray-500 mt-1">{item.cliente_nome_empresarial}</p>
                                                                    <p className="text-xs text-gray-400 mt-0.5">{formatCNPJ(item.cliente_cnpj)}</p>
                                                                </div>
                                                                <div className="flex space-x-2 ml-2">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            viewFatura(item)
                                                                        }}
                                                                        className={`p-2 ${colorClasses.headerIconBg} ${colorClasses.headerIconText} rounded-lg hover:opacity-80 transition-all duration-200`}
                                                                        title="Visualizar detalhes"
                                                                    >
                                                                        <EyeIcon className="w-4 h-4" />
                                                                    </button>

                                                                    {downloadFaturaPDF && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation()
                                                                                handleDownload(item)
                                                                            }}
                                                                            disabled={downloadLoadingId === item.id}
                                                                            className={`p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all duration-200 disabled:opacity-50`}
                                                                            title="Baixar fatura"
                                                                        >
                                                                            {downloadLoadingId === item.id ? (
                                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                                                                            ) : (
                                                                                <ArrowDownTrayIcon className="w-4 h-4" />
                                                                            )}
                                                                        </button>
                                                                    )}

                                                                    {geracaoNovaFatura && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation()
                                                                                handleGerarNovaVersao(item)
                                                                            }}
                                                                            disabled={geracaoLoadingId === item.id}
                                                                            className={`p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-all duration-200 disabled:opacity-50`}
                                                                            title="Gerar nova versão"
                                                                        >
                                                                            {geracaoLoadingId === item.id ? (
                                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                                                                            ) : (
                                                                                <ArrowPathIcon className="w-4 h-4" />
                                                                            )}
                                                                        </button>
                                                                    )}

                                                                    {editFatura && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation()
                                                                                handlerUpdate(item)
                                                                            }}
                                                                            className={`p-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-all duration-200`}
                                                                            title="Editar fatura"
                                                                        >
                                                                            <PencilIcon className="w-4 h-4" />
                                                                        </button>
                                                                    )}

                                                                    {deleteFatura && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation()
                                                                                handlerDelete(item)
                                                                            }}
                                                                            className={`p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all duration-200`}
                                                                            title="Excluir fatura"
                                                                        >
                                                                            <TrashIcon className="w-4 h-4" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2 pl-2">
                                                                <div className="flex items-start">
                                                                    <InformationCircleIcon className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">Valor Total:</span>
                                                                        <span className="text-xs font-semibold text-green-800 ml-2">{formatCurrency(item.total)}</span>
                                                                    </div>
                                                                </div>
                                                                {item.data_fatura && (
                                                                    <div className="flex items-start">
                                                                        <CalendarIcon className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                                                                        <div className="flex-1 min-w-0">
                                                                            <span className="text-xs font-medium text-gray-500">Data da Fatura:</span>
                                                                            <span className="text-xs text-gray-900 ml-2">{formatDateOptional(item.data_fatura)}</span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div className="flex items-start">
                                                                    <InformationCircleIcon className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">Email:</span>
                                                                        <span className="text-xs text-gray-900 ml-2">{item.cliente_email}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-start">
                                                                    <InformationCircleIcon className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">Telefone:</span>
                                                                        <span className="text-xs text-gray-900 ml-2">{formatPhone(item.cliente_telefone)}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="px-6 py-8 text-center text-gray-500">
                                    <ArchiveBoxIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma {title} encontrada</h3>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {data && data.length > 0 && (
                <div className="flex items-center justify-between bg-white rounded-2xl shadow-lg border border-gray-200 p-4 mt-6">
                    <div className="flex space-x-2">
                        <button
                            onClick={previous}
                            disabled={page === 0}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <span className="px-3 py-2 text-sm font-medium text-gray-700">
                            Página {page + 1}
                        </span>
                        <button
                            onClick={next}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Modal de Seleção de Download */}
            <Transition.Root show={showDownloadModal} as={Fragment}>
                <Dialog as="div" className="relative z-[60]" onClose={closeDownloadModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-[60] overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                                    <div className="absolute right-0 top-0 pr-4 pt-4">
                                        <button
                                            type="button"
                                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                            onClick={closeDownloadModal}
                                        >
                                            <span className="sr-only">Fechar</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                        </button>
                                    </div>

                                    <div className="text-center mb-6">
                                        <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${downloadType === 'geracao' ? 'bg-green-100' : 'bg-blue-100'} mb-4`}>
                                            {downloadType === 'geracao' ? (
                                                <ArrowPathIcon className="h-6 w-6 text-green-600" />
                                            ) : (
                                                <ArrowDownTrayIcon className="h-6 w-6 text-blue-600" />
                                            )}
                                        </div>
                                        <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                                            {downloadType === 'geracao' ? 'Nova Versão Gerada!' : 'Arquivos Disponíveis'}
                                        </Dialog.Title>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Selecione qual formato deseja baixar:
                                        </p>
                                    </div>

                                    <div className="space-y-3">
                                        {downloadData?.pdf && (
                                            <button
                                                onClick={() => executeDownload('pdf')}
                                                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-300 transition-all duration-200"
                                            >
                                                <DocumentTextIcon className="w-6 h-6" />
                                                <span className="font-medium">Baixar PDF</span>
                                            </button>
                                        )}

                                        {downloadData?.xml && (
                                            <button
                                                onClick={() => executeDownload('xml')}
                                                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
                                            >
                                                <CodeBracketIcon className="w-6 h-6" />
                                                <span className="font-medium">Baixar XML</span>
                                            </button>
                                        )}

                                        {downloadData?.pdf && downloadData?.xml && (
                                            <button
                                                onClick={() => executeDownload('ambos')}
                                                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300 transition-all duration-200"
                                            >
                                                <ArrowDownTrayIcon className="w-6 h-6" />
                                                <span className="font-medium">Baixar Ambos</span>
                                            </button>
                                        )}
                                    </div>

                                    <div className="mt-6">
                                        <button
                                            type="button"
                                            onClick={closeDownloadModal}
                                            className="w-full inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-all"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    )
}

export default FaturasTable

